/**
 * O CATÁLOGO de Configurações: quais blocos existem, em que cartão cada um mora e quem o
 * desenha.
 *
 * A tela tinha um menu lateral com os vinte e um blocos, todos no mesmo formato: quem
 * chegava lia a lista inteira para achar um assunto. Agora a entrada são seis cartões, e
 * os blocos de um cartão ficam empilhados dentro dele. O catálogo é dado, não JSX, por
 * três motivos: a entrada e a página do cartão leem a MESMA lista (um item sem painel é
 * impossível), a barra de endereços guarda o cartão e o bloco abertos (`?secao=`,
 * `?bloco=`), e um teste consegue percorrer o inventário inteiro para provar que nenhum
 * bloco de antes sumiu.
 */
import type { SymbolCodepoints } from '@umodeapporg/ui/components/UI/MaterialSymbol';

import {
  FALE_ABA_RECEBIDAS,
  SECAO_FALE as AJUDA_SECAO_FALE,
  SECAO_PARAM as AJUDA_SECAO_PARAM,
} from '@/app/ajuda/helpers/ajudaNavegacao';
import { MAPA_CARTOES_ABERTA, MAPA_CARTOES_PARAM } from '@/app/mapa/helpers/parametrosDoMapa';
import { ACTIVE_TAB_PARAM } from '@/domain/common/hooks/useActiveTabParam';

/** Quem desenha o bloco: cada painel é um componente da tela. */
export type TSettingsPanel = 'config' | 'historico';

/**
 * Os grupos do formulário de configuração, exatamente como o PATCH os aceita
 * (`IUpdateConfigPayload`). Um bloco declara quais grupos o Salvar DELE envia.
 */
export type TConfigGroup =
  | 'prazo'
  | 'jornada'
  | 'apontamento'
  | 'celulas'
  | 'programacao'
  | 'liberacao'
  | 'cobranca'
  | 'maquinas'
  | 'mapa'
  | 'lixeira'
  | 'cronometragem'
  | 'balanceamento'
  | 'ocupacao'
  | 'fila'
  | 'importacao'
  | 'sequencias'
  | 'trocaCelula';

export interface ISettingsBlock {
  /** O que vai na barra de endereços: `/settings?bloco=<id>`. */
  id: string;
  labelKey: string;
  panel: TSettingsPanel;
  /**
   * Os grupos que o Salvar deste bloco envia. Vazio quer dizer que o bloco não tem Salvar
   * próprio: ou ele é só leitura (as regras fixas), ou cada linha dele já grava sozinha
   * (o histórico).
   */
  grupos: ReadonlyArray<TConfigGroup>;
}

/**
 * UM cartão da entrada: o tema, o que ele guarda e como ele se apresenta.
 *
 * A entrada mostra seis cartões em vez dos vinte e um blocos: quem chega escolhe o
 * ASSUNTO, e só dentro dele vê os parâmetros.
 */
export interface ISettingsSection {
  /** O que vai na barra de endereços: `/settings?secao=<id>`. */
  id: string;
  labelKey: string;
  /** Uma linha dizendo o que este cartão decide, abaixo do nome. */
  subtitleKey: string;
  icon: SymbolCodepoints;
  blocos: ReadonlyArray<ISettingsBlock>;
}

/** A chave do bloco aberto na barra de endereços. */
export const BLOCO_PARAM = 'bloco';

/** A chave do cartão aberto na barra de endereços. */
export const SECAO_PARAM = 'secao';

/**
 * O cartão da Implantação: ele não tem blocos de parâmetro, e sim a lista do que falta
 * validar. Só quem pode ALTERAR as configurações o vê.
 */
export const SECAO_IMPLANTACAO = 'implantacao';

/**
 * O bloco que abre quando o endereço não pede nenhum: o cálculo de prazo, que é o mais
 * consultado e era o que a tela já abria.
 */
export const BLOCO_PADRAO: ISettingsBlock = {
  id: 'prazo',
  labelKey: 'settings.indice.bloco.prazo',
  panel: 'config',
  grupos: ['prazo'],
};

/**
 * Os SEIS cartões da entrada, na ordem em que a pessoa os procura: primeiro o que a
 * fábrica É, depois o que decide data, depois o que acontece no chão, e por fim o que
 * avisa, o que já aconteceu (ou foi excluído) e o que ainda espera confirmação.
 *
 * O nome, o logo, os papéis e o acesso de cada pessoa NÃO moram aqui: são da conta no
 * dashboard da uMode, e o tema e o idioma estão no cabeçalho.
 */
export const SETTINGS_SECTIONS: ReadonlyArray<ISettingsSection> = [
  {
    id: 'fabrica',
    labelKey: 'settings.indice.secao.fabrica',
    subtitleKey: 'settings.secao.subtitulo.fabrica',
    icon: 'factory',
    blocos: [
      { id: 'jornada', labelKey: 'settings.indice.bloco.jornada', panel: 'config', grupos: ['jornada'] },
      { id: 'celulas', labelKey: 'settings.indice.bloco.celulas', panel: 'config', grupos: ['celulas'] },
      { id: 'maquinas', labelKey: 'settings.indice.bloco.maquinas', panel: 'config', grupos: ['maquinas'] },
    ],
  },
  {
    id: 'prazo',
    labelKey: 'settings.indice.secao.prazo',
    subtitleKey: 'settings.secao.subtitulo.prazo',
    icon: 'event',
    blocos: [
      {
        id: 'programacao',
        labelKey: 'settings.indice.bloco.programacao',
        panel: 'config',
        grupos: ['programacao'],
      },
      BLOCO_PADRAO,
      { id: 'fonteTempo', labelKey: 'settings.indice.bloco.fonteTempo', panel: 'config', grupos: ['prazo'] },
      // A folga do farol e os cortes da gravidade moram no grupo `prazo`, e os dois cortes
      // de ocupação no grupo `ocupacao`: na tela eles são UMA pergunta, então o bloco
      // grava os dois grupos de uma vez.
      { id: 'ocupacao', labelKey: 'settings.indice.bloco.ocupacao', panel: 'config', grupos: ['prazo', 'ocupacao'] },
      { id: 'trocaCelula', labelKey: 'settings.indice.bloco.trocaCelula', panel: 'config', grupos: ['trocaCelula'] },
      { id: 'regrasFixas', labelKey: 'settings.indice.bloco.regrasFixas', panel: 'config', grupos: [] },
    ],
  },
  {
    id: 'chaoDeFabrica',
    labelKey: 'settings.indice.secao.chaoDeFabrica',
    subtitleKey: 'settings.secao.subtitulo.chaoDeFabrica',
    icon: 'precision_manufacturing',
    blocos: [
      { id: 'apontamento', labelKey: 'settings.indice.bloco.apontamento', panel: 'config', grupos: ['apontamento'] },
      {
        id: 'cronometragem',
        labelKey: 'settings.indice.bloco.cronometragem',
        panel: 'config',
        grupos: ['cronometragem'],
      },
      { id: 'liberacao', labelKey: 'settings.indice.bloco.liberacao', panel: 'config', grupos: ['liberacao'] },
      {
        id: 'balanceamento',
        labelKey: 'settings.indice.bloco.balanceamento',
        panel: 'config',
        grupos: ['balanceamento'],
      },
      { id: 'sequencias', labelKey: 'settings.indice.bloco.sequencias', panel: 'config', grupos: ['sequencias'] },
    ],
  },
  {
    id: 'avisos',
    labelKey: 'settings.indice.secao.avisos',
    subtitleKey: 'settings.secao.subtitulo.avisos',
    icon: 'notifications_active',
    blocos: [
      { id: 'fila', labelKey: 'settings.indice.bloco.fila', panel: 'config', grupos: ['fila'] },
      { id: 'cobranca', labelKey: 'settings.indice.bloco.cobranca', panel: 'config', grupos: ['cobranca'] },
      { id: 'importacao', labelKey: 'settings.indice.bloco.importacao', panel: 'config', grupos: ['importacao'] },
    ],
  },
  {
    id: 'historico',
    labelKey: 'settings.indice.secao.historico',
    subtitleKey: 'settings.secao.subtitulo.historico',
    icon: 'history',
    blocos: [
      { id: 'historico', labelKey: 'settings.indice.bloco.historico', panel: 'historico', grupos: [] },
      // A lixeira é o outro lado do histórico: o que foi excluído, e por quanto tempo fica guardado.
      { id: 'lixeira', labelKey: 'settings.indice.bloco.lixeira', panel: 'config', grupos: ['lixeira'] },
    ],
  },
  {
    id: SECAO_IMPLANTACAO,
    labelKey: 'settings.indice.secao.implantacao',
    subtitleKey: 'settings.secao.subtitulo.implantacao',
    icon: 'rocket_launch',
    // Sem blocos: a página deste cartão é a lista do que falta validar.
    blocos: [],
  },
];

/** Todos os blocos, na ordem dos cartões. */
export const SETTINGS_BLOCKS: ReadonlyArray<ISettingsBlock> = SETTINGS_SECTIONS.flatMap((secao) => secao.blocos);

/**
 * Os endereços de ANTES continuam valendo. A tela tinha abas (`?activeTab=`) e um bloco
 * que virou parte de outro, então um link já mandado (ou o botão "Resolver" da
 * Implantação, que aponta para `?bloco=feriados`) tem de cair no lugar certo em vez de
 * abrir a tela no bloco padrão, calada.
 */
const ALIAS_DE_BLOCO: Readonly<Record<string, string>> = {
  // Os feriados viraram a segunda metade de "Jornada e feriados".
  feriados: 'jornada',
  // As abas de antes, pelo valor que elas tinham em `?activeTab=`.
  product: BLOCO_PADRAO.id,
  versoes: 'historico',
};

/**
 * Os blocos que SAÍRAM das Configurações, e para onde o endereço antigo leva.
 *
 * Eles foram para perto do que decidem: o que aparece nos cartões do Mapa é uma janela do
 * próprio Mapa, e as sugestões recebidas são uma aba da Ajuda. O link de antes continua
 * valendo porque a tela o reenvia, em vez de abrir uma página que não fala mais daquilo.
 */
export const BLOCO_QUE_MUDOU_DE_CASA: Readonly<Record<string, string>> = {
  mapaCartao: `/mapa?${MAPA_CARTOES_PARAM}=${MAPA_CARTOES_ABERTA}`,
  sugestoes: `/ajuda?${AJUDA_SECAO_PARAM}=${AJUDA_SECAO_FALE}&${ACTIVE_TAB_PARAM}=${FALE_ABA_RECEBIDAS}`,
};

/** O bloco de um id, ou `null` quando o id não existe (link digitado à mão). */
export function acharBloco(id: string): ISettingsBlock | null {
  const procurado = id.trim();
  if (procurado === '') {
    return null;
  }
  const direto = SETTINGS_BLOCKS.find((bloco) => bloco.id === procurado);
  if (direto !== undefined) {
    return direto;
  }
  const apelido = ALIAS_DE_BLOCO[procurado];
  return apelido === undefined ? null : (SETTINGS_BLOCKS.find((bloco) => bloco.id === apelido) ?? null);
}

/**
 * O bloco que a tela abre, lendo o endereço inteiro: o `?bloco=` manda, e `?activeTab=`
 * é a segunda chance, para o link antigo que só nomeava a aba. `null` é "nenhum pedido",
 * e aí a tela mostra a entrada dos cartões.
 */
export function blocoDoEndereco(bloco: string, activeTab: string): ISettingsBlock | null {
  return acharBloco(bloco) ?? acharBloco(activeTab);
}

/** O cartão de um id, ou `null` quando o id não existe (link digitado à mão). */
export function acharSecao(id: string): ISettingsSection | null {
  const procurado = id.trim();
  return procurado === '' ? null : (SETTINGS_SECTIONS.find((secao) => secao.id === procurado) ?? null);
}

/** O cartão onde um bloco mora. */
export function secaoDoBloco(bloco: ISettingsBlock): ISettingsSection | null {
  return SETTINGS_SECTIONS.find((secao) => secao.blocos.some((candidato) => candidato.id === bloco.id)) ?? null;
}

/**
 * O cartão que a tela abre, lendo o endereço inteiro.
 *
 * Os links de ANTES apontavam para um bloco (`?bloco=`, `?activeTab=`) ou para a lista da
 * implantação (`?implantacao=aberta`), e nenhum deles nomeia cartão: eles abrem o cartão
 * de quem pediram, com o bloco pedido aberto dentro dele. `null` é "nenhum pedido", e aí
 * a tela mostra a entrada.
 */
export function secaoDoEndereco(secao: string, bloco: ISettingsBlock | null, implantacao: boolean): ISettingsSection | null {
  const pedida = acharSecao(secao);
  if (pedida !== null) {
    return pedida;
  }
  if (bloco !== null) {
    return secaoDoBloco(bloco);
  }
  return implantacao ? acharSecao(SECAO_IMPLANTACAO) : null;
}
